import styled from 'styled-components';

import { colorCss, sizeCss, StyleValueAxis, StyleValueFramePositioning } from 'lib/inquiryStyles';
import { HorizontalStackComponent } from 'lib/interfaces/ui-components';
import { HolderClient } from 'verify/lib/clients/HolderClient';

export const StackContainer = styled.div<{
  styles: HorizontalStackComponent['styles'];
}>(({ styles }) => {
  const marginLeft =
    styles?.['frame-positioning']['base']['base'] === StyleValueFramePositioning.EdgeToEdge
      ? `-${HolderClient.widgetPadding.left}px`
      : sizeCss(styles?.margin.base.base?.left);
  const marginRight =
    styles?.['frame-positioning']['base']['base'] === StyleValueFramePositioning.EdgeToEdge
      ? `-${HolderClient.widgetPadding.right}px`
      : sizeCss(styles?.margin.base.base?.right);

  return {
    display: 'flex',
    flexWrap: 'nowrap',
    borderStyle: 'solid',

    width: styles?.width?.base?.base?.value ?? `calc(100% - ${marginLeft} - ${marginRight})`,
    marginTop: sizeCss(styles?.margin.base.base?.top),
    marginBottom: sizeCss(styles?.margin.base.base?.bottom),
    marginLeft,
    marginRight,

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    flexDirection: `${styles.axis.base.base === StyleValueAxis.Vertical ? 'column' : 'row'}`,
    gap: sizeCss(styles?.gap.base.base),
    // NOTE: this no-ops if columns are ratio-sized.
    justifyContent: styles?.justify.base.base,
    paddingTop: sizeCss(styles?.padding.base.base?.top),
    paddingBottom: sizeCss(styles?.padding.base.base?.bottom),
    paddingLeft: sizeCss(styles?.padding.base.base?.left),
    paddingRight: sizeCss(styles?.padding.base.base?.right),
    backgroundColor: colorCss(styles?.['background-color'].base.base),
    borderColor: colorCss(styles?.['border-color'].base.base),
    borderTopWidth: sizeCss(styles?.['border-width'].base.base?.top),
    borderBottomWidth: sizeCss(styles?.['border-width'].base.base?.bottom),
    borderLeftWidth: sizeCss(styles?.['border-width'].base.base?.left),
    borderRightWidth: sizeCss(styles?.['border-width'].base.base?.right),
    borderRadius: sizeCss(styles?.['border-radius'].base.base),
  };
});

// NOTE: we use flex-direction: column; to replicate UiComponentView's layout.
// This lets components behave the same inside and outside Stacks.
// NOTE: each Column should have a SINGLE child. flex is SOLELY to allow for
// uniform behavior for alignment.
export const StackChild = styled.div<{
  styles: HorizontalStackComponent['styles'];
  index: number;
}>(({ styles, index }) => {
  const flexGrow = styles?.['child-sizes'].base.base?.[index] ?? 0;
  const useIntrinsicSizing = flexGrow === 0;
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const isVertical = styles.axis.base.base === StyleValueAxis.Vertical;

  return {
    display: 'flex',

    flexDirection: 'column',
    // styles.alignment is used to position flex-children on the horizontal axis.
    // When styles.axis is vertical, styles.alignment should be ignored, because items should stretch to fill the width of the container like they do at the top level.
    justifyContent: isVertical ? undefined : styles?.alignment.base.base,
    flexGrow,
    // Don't shrink if using intrinsic sizing
    flexShrink: useIntrinsicSizing ? 0 : 1,
    // This makes flex-grow act the same as grid fr by removing all "intrinsic sizing"
    flexBasis: useIntrinsicSizing ? 'initial' : 0,
    ':empty': {
      // don't take up space if no children
      // note that text nodes are also considered children, even though they aren't HTML nodes
      flexGrow: 0,
      // counteract gap spacing for hidden child
      margin: `calc(${sizeCss(styles?.gap.base.base)} / -2)`,
    },
    // allow flex children to scale to fit within the container
    wordBreak: 'break-word',
    // images and ImageLocal flex to available width within the container
    '> img, [role="img"] svg': {
      maxWidth: '100% !important',
      height: 'auto !important',
      objectFit: 'contain',
      display: 'block',
    },
  };
});
